import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePortalTarget } from '../../provider/UiContext';
import { overflowTextOf } from '../dataTableOverflow';

export interface IDataTableOverflowTipProps {
    /** Контейнер таблицы: наведение ловим одним обработчиком на нём. */
    containerRef: RefObject<HTMLElement | null>;
}

/** Пауза перед показом: подсказка не должна выскакивать на каждую ячейку по пути курсора. */
const OPEN_DELAY = 450;
/** Соседняя ячейка при уже открытой подсказке — почти сразу (поведение тултипов). */
const NEXT_DELAY = 120;
/** Отступ подсказки от ячейки и от края экрана. */
const GAP = 6;
const EDGE = 8;

/**
 * Подсказка с полным текстом обрезанной ячейки.
 *
 * Живёт одним обработчиком на всей таблице, а не оболочкой вокруг каждой ячейки:
 * в списке из сотен ячеек любая обвязка на ячейку стоит кадра прокрутки. По той
 * же причине состояние держит этот компонент, а не таблица — наведение
 * перерисовывает только подсказку.
 */
const DataTableOverflowTip = ({ containerRef }: IDataTableOverflowTipProps) => {
    const portalTarget = usePortalTarget();
    const [tip, setTip] = useState<{ text: string; rect: DOMRect } | null>(null);
    const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
    const tipRef = useRef<HTMLDivElement>(null);
    const cellRef = useRef<HTMLElement | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const openRef = useRef(false);

    const hide = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        cellRef.current = null;
        openRef.current = false;
        setTip(null);
    }, []);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const schedule = (cell: HTMLElement, delay: number) => {
            timerRef.current = setTimeout(() => {
                // за время паузы строка могла уехать под курсором (прокрутка, догрузка)
                if (!cell.isConnected) return;
                const text = overflowTextOf(cell);
                if (!text) return;
                openRef.current = true;
                setTip({ text, rect: cell.getBoundingClientRect() });
            }, delay);
        };

        const onOver = (e: MouseEvent) => {
            // после тапа браузер тоже шлёт mouseover — на сенсорном вводе подсказка не нужна
            const source = (e as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } })
                .sourceCapabilities;
            if (source?.firesTouchEvents) return;
            const cell = (e.target as HTMLElement | null)?.closest?.<HTMLElement>('.dtb__cell') ?? null;
            if (cell === cellRef.current) return;
            if (timerRef.current) clearTimeout(timerRef.current);
            const wasOpen = openRef.current;
            openRef.current = false;
            cellRef.current = cell;
            if (wasOpen) setTip(null);
            if (cell) schedule(cell, wasOpen ? NEXT_DELAY : OPEN_DELAY);
        };

        container.addEventListener('mouseover', onOver);
        container.addEventListener('mouseleave', hide);
        // прокрутка, клик и клавиатура уводят подсказку от её ячейки — прячем
        window.addEventListener('scroll', hide, true);
        window.addEventListener('wheel', hide, { passive: true });
        window.addEventListener('pointerdown', hide, true);
        window.addEventListener('keydown', hide, true);
        return () => {
            container.removeEventListener('mouseover', onOver);
            container.removeEventListener('mouseleave', hide);
            window.removeEventListener('scroll', hide, true);
            window.removeEventListener('wheel', hide);
            window.removeEventListener('pointerdown', hide, true);
            window.removeEventListener('keydown', hide, true);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [containerRef, hide]);

    // Размеры подсказки известны только после отрисовки, поэтому стартовое место
    // (под ячейкой, по её левому краю) правится в layout-эффекте — до показа кадра,
    // так что переезда не видно: у нижнего края экрана подсказка встаёт над ячейкой,
    // у правого — прижимается к экрану.
    useLayoutEffect(() => {
        if (!tip || !tipRef.current) {
            setPos(null);
            return;
        }
        const { width, height } = tipRef.current.getBoundingClientRect();
        const maxLeft = Math.max(EDGE, window.innerWidth - width - EDGE);
        const left = Math.min(Math.max(EDGE, tip.rect.left), maxLeft);
        const below = tip.rect.bottom + GAP;
        const top = below + height > window.innerHeight - EDGE ? Math.max(EDGE, tip.rect.top - height - GAP) : below;
        setPos({ left, top });
    }, [tip]);

    if (!tip) return null;

    return createPortal(
        <div
            ref={tipRef}
            role="tooltip"
            className="dtb-tip"
            style={{ left: pos?.left ?? tip.rect.left, top: pos?.top ?? tip.rect.bottom + GAP }}
        >
            {tip.text}
        </div>,
        portalTarget ?? document.body,
    );
};

export default DataTableOverflowTip;
